import { useEffect, useMemo, useState } from "react";
import type { Pin } from "@centerline/core/viewer";
import { api } from "./api";
import { href } from "./router";
import { useOfflineStatus } from "./offline";

export interface PunchRow { pin: Pin; sheet: string; sheetIndex: number; docName: string }

/** Punch List tab: every Punch pin on the job, filterable, with a PDF export. */
export function PunchListTab({ projectId, tick }: { projectId: string; tick: number }) {
  const [items, setItems] = useState<PunchRow[] | null>(null);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("open");
  const [sheet, setSheet] = useState("");
  const [assignee, setAssignee] = useState("");
  const { online } = useOfflineStatus();
  useEffect(() => { api.punchList(projectId).then((r) => { setItems(r.items); setErr(""); }).catch((e) => setErr((e as Error).message)); }, [projectId, tick]);
  const all = items ?? [];
  const sheets = useMemo(() => [...new Set(all.map((i) => i.sheet))], [all]);
  const people = useMemo(() => [...new Set(all.map((i) => i.pin.assignee ?? "").filter(Boolean))].sort(), [all]);
  const shown = all.filter((i) => (status === "open" ? i.pin.status !== "closed" : status === "closed" ? i.pin.status === "closed" : true)
    && (!sheet || i.sheet === sheet) && (!assignee || (assignee === "-" ? !i.pin.assignee : (i.pin.assignee ?? "").toLowerCase() === assignee.toLowerCase())));
  const q = new URLSearchParams({ ...(status !== "all" ? { status } : {}), ...(sheet ? { sheet } : {}), ...(assignee && assignee !== "-" ? { assignee } : {}) }).toString();
  const today = new Date().toISOString().slice(0, 10);
  return (
    <section className="punch-tab" data-testid="punch-tab">
      <div className="row wrap punch-filters">
        <label>Status<select value={status} data-testid="punch-f-status" onChange={(e) => setStatus(e.target.value)}><option value="open">Open</option><option value="closed">Closed</option><option value="all">All</option></select></label>
        <label>Sheet<select value={sheet} data-testid="punch-f-sheet" onChange={(e) => setSheet(e.target.value)}><option value="">All sheets</option>{sheets.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label>Assigned to<select value={assignee} data-testid="punch-f-assignee" onChange={(e) => setAssignee(e.target.value)}><option value="">Anyone</option>{people.map((p) => <option key={p}>{p}</option>)}<option value="-">Not assigned</option></select></label>
        <span className="grow" />
        {online
          ? <a className="button-like primary" data-testid="punch-pdf" href={`/api/projects/${projectId}/punch-list.pdf${q ? `?${q}` : ""}`} target="_blank" rel="noreferrer">⬇ Export PDF ({shown.length})</a>
          : <span className="muted" data-testid="punch-pdf-offline"><button type="button" disabled>⬇ Export PDF</button> PDF export needs signal</span>}
      </div>
      {err && <p className="error">{err}</p>}
      {items === null && !err && <p className="muted">Loading…</p>}
      {items && !shown.length && <p className="muted">{all.length ? "No punch items match these filters." : "No punch items yet. On a sheet, pick ✅ Punch (J) and tap the spot that needs fixing. Photo pins can be turned into punch items from their Details."}</p>}
      <ol className="punch-list">
        {shown.map((i) => {
          const p = i.pin, img = p.attachments.find((a) => a.contentType.startsWith("image/"));
          const late = p.status !== "closed" && p.dueDate && p.dueDate < today;
          return (
            <li key={p.id} className={p.status === "closed" ? "closed" : ""}>
              <a href={href({ name: "viewer", id: p.documentId, page: i.sheetIndex + 1, pin: p.id })} data-testid="punch-row">
                {img ? <img src={api.pinFileUrl(p.id, img.id)} alt="" loading="lazy" /> : <span className="punch-noimg">✅</span>}
                <div className="punch-main">
                  <b>{p.title || "(untitled)"}</b>
                  <small>Sheet {i.sheet}{p.description ? ` · ${p.description}` : ""}</small>
                  <small>{p.assignee ? `→ ${p.assignee}` : "Not assigned"}{p.dueDate ? ` · due ${p.dueDate}` : ""}{late ? " · LATE" : ""}</small>
                </div>
                <span className={`punch-badge ${p.status === "closed" ? "done" : late ? "warn" : ""}`}>{p.status === "closed" ? `Closed${p.closedBy ? ` · ${p.closedBy}` : ""}` : p.status === "in progress" ? "In progress" : "Open"}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
